import styled from '@emotion/styled';
import { darken } from 'polished';
import { breakpoints, colors, dimensions } from '../../styles/variables';
import illustration from '../../assets/illustration-background.svg';
import backgroundTop from '../../assets/backgrounds/biscuit-top.svg';
import backgroundBottom from '../../assets/backgrounds/biscuit-bottom.svg';
import { ButtonType, FeatureType } from './types';
import checkmarkIcon from '../../assets/icon-checkmark.svg';
import crossIcon from '../../assets/icon-cross.svg';
import plusIcon from '../../assets/icon-plus.svg';
import PricingTop from '../../assets/backgrounds/pricing-top.inline.svg';
import PricingBottom from '../../assets/backgrounds/pricing-bottom.inline.svg';
import pricingCenter from '../../assets/backgrounds/pricing-center.svg';
import pricingColumnsDivider from '../../assets/pricing-columns-divider1.svg';
import pricingBadge from '../../assets/pricing-badge.svg';
import pricingMixBadge from '../../assets/pricing-mix-badge.svg';
import { Visibility } from '../../types';
import DealIllustrationRaw from '../../assets/illustration-deal.inline.svg';

export const Wrapper = styled.section<Visibility>`
  opacity: 0;
  display: flex;
  margin-top: 85px;
  position: relative;
  align-items: center;
  flex-direction: column;
  transform: translateY(10%);
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;

  ${({ isVisible }) =>
    isVisible &&
    `
    opacity: 1;
    transform: translateY(0);
  `}
`;

export const StickyDetection = styled.div`
  width: 100%;
  height: 1px;
  padding-top: 450px;
  margin-bottom: -1px;
  position: absolute;
  pointer-events: none;
`;

const TOP_BACKGROUND_HEIGHT = '1.48vw';
const BOTTOM_BACKGROUND_HEIGHT = '1.88vw';

export const BackgroundWrapper = styled.div`
  width: 100%;
  height: 700px;
  margin-top: -${TOP_BACKGROUND_HEIGHT};
  position: absolute;
  pointer-events: none;
  overflow: hidden;
`;

export const Background = styled.div`
  width: 100vw;
  height: 599px;
  position: absolute;
  margin-top: ${TOP_BACKGROUND_HEIGHT};
  z-index: -2;
  background-color: ${colors.biscuit};
  background-size: cover;
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;

  &:before,
  &:after {
    content: '';
    width: 100%;
    position: absolute;
    left: 0;
    background-size: contain;
  }
  &:before {
    height: ${TOP_BACKGROUND_HEIGHT};
    top: calc(-${TOP_BACKGROUND_HEIGHT} + 2px);
    background: url(${backgroundTop}) no-repeat center 0;

    @media screen and (max-width: ${breakpoints.lg}px) {
      margin-top: 1px;
      transform: scale(1.1);
    }
  }
  &:after {
    height: ${BOTTOM_BACKGROUND_HEIGHT};
    bottom: -${BOTTOM_BACKGROUND_HEIGHT};
    background: url(${backgroundBottom}) no-repeat center 0;

    @media screen and (max-width: ${breakpoints.lg}px) {
      transform: scale(1.1);
    }
  }
`;

export const BackgroundIllustrations = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-start;
  justify-content: center;

  &:after {
    content: '';
    width: 1514px;
    height: 636px;
    position: absolute;
    top: -43px;
    background: url(${illustration}) no-repeat center 0;
    background-size: contain;
    transform: translateX(3px);
  }
`;

export const Switcher = styled.div`
  display: flex;
  margin-top: 25px;
  position: relative;
  align-items: center;
  justify-content: center;
`;

interface SwitcherUIProps {
  isMonthly: boolean;
}

export const SwitcherUI = styled.i<SwitcherUIProps>`
  width: 55px;
  height: 34px;
  border: 3px solid ${colors.navyBlue};
  background-color: ${colors.white};
  border-radius: 24px;
  position: relative;

  &:before {
    content: '';
    width: 24px;
    height: 24px;
    border-radius: 50%;
    position: absolute;
    top: 2px;
    left: 3px;
    border: 3px solid #ffd858;
    background-color: #ffd858;
    transform: translateX(${({ isMonthly }) => (isMonthly ? 19 : 0)}px);
    transition: 0.1s transform ease-in-out, 0.1s border ease-in-out;
  }
`;

export const SwitcherCopy = styled.span`
  margin: 0 24px;
  color: ${colors.navyBlue};
  font-size: ${dimensions.fontSize.medium}px;
  font-weight: 900;
`;

export const SwitcherInput = styled.input`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  appearance: none;
  cursor: pointer;
  border: 0;

  &:focus,
  &:hover {
    outline: 0;

    ~ ${SwitcherUI} {
      &:before {
        border-color: ${colors.navyBlue};
      }
    }
  }
`;

export const ListWrapper = styled.div`
  display: flex;
  width: 100%;
  margin: 22px auto 0 auto;
  position: relative;
  flex-direction: column;
  align-items: center;

  @media screen and (max-width: 1020px) {
    max-width: 100%;
    width: calc(100% - 60px);
    margin-right: auto;
    margin-left: auto;
  }
`;

export const ListTopBackground = styled(PricingTop)`
  max-width: 956px;
  margin-bottom: -5px;
`;

export const ListBottomBackground = styled(PricingBottom)`
  max-width: 956px;
  margin-top: -4px;
`;

interface ListUIProps {
  isMixPricing: boolean;
}
export const List = styled.ul<ListUIProps>`
  display: grid;
  max-width: 960px;
  width: 100%;
  margin: 0;
  padding: 0 0 98px 0;
  position: relative;
  grid-template-columns: repeat(2, 1fr);
  background: url(${pricingCenter}) repeat-y 0 180px;
  background-size: 100%;

  @media screen and (max-width: 1020px) {
    display: flex;
    max-width: 100%;
    flex-direction: column;
  }

  &:before {
    content: '';
    width: 147px;
    height: 146px;
    position: absolute;
    top: -148px;
    right: 5px;
    background: url(${({ isMixPricing }) => (isMixPricing ? pricingMixBadge : pricingBadge)}) no-repeat 0 0;
    background-size: contain;

    @media screen and (max-width: 1020px) {
      display: none;
    }
    @-moz-document url-prefix() {
      top: -144px;
      right: 7px;
    }
  }
  &:after {
    content: '';
    width: 16px;
    height: 1235px;
    position: absolute;
    top: -175px;
    left: 50%;
    background: url(${pricingColumnsDivider}) no-repeat 0 0;
    background-size: contain;
    transform: translateX(-50%);

    @media screen and (max-width: 1020px) {
      display: none;
    }
  }
`;

export const Heading = styled.header`
  text-align: center;

  @media screen and (max-width: ${breakpoints.sm}px) {
    margin-top: 20px;
  }
`;

export const Title = styled.h2`
  margin: 0;
  font-size: ${dimensions.fontSize.heading}px;
  line-height: ${dimensions.lineHeight.heading};
  font-weight: 900;
`;

export const SubTitle = styled.h5`
  margin: -4px 0 0 0;
  padding-left: 10px;
  color: ${colors.lightGray};
  font-size: ${dimensions.fontSize.regular}px;
  font-weight: 400;
  line-height: ${dimensions.lineHeight.regular};
`;

interface ButtonProps {
  type: ButtonType;
}

export const Price = styled.div`
  display: flex;
  margin-top: 22px;
  text-align: center;
  justify-content: center;
  color: ${colors.navyBlue};
  font-size: ${dimensions.fontSize.medium}px;
  line-height: ${dimensions.lineHeight};
`;

export const PriceVault = styled.small`
  margin-top: 16px;
  margin-right: 4px;
  font-size: 1em;
`;

export const PriceAmount = styled.span`
  font-size: 4em;
  font-weight: 900;
`;

export const PricePeriod = styled.span`
  margin-top: auto;
  margin-bottom: 12px;
  margin-left: 0px;
`;

export const PricingColumn = styled.li`
  margin-top: -180px;
  margin-bottom: -180px;
  padding-top: 60px;
  list-style: none;

  @media screen and (max-width: 1020px) {
    margin-top: 0;
    margin-bottom: 0;
  }

  &:first-of-type {
    padding-left: 6px;

    @media screen and (max-width: 1020px) {
      margin-top: -180px;
    }
    @media screen and (max-width: 768px) {
      margin-top: -100px;
    }
    @media screen and (max-width: ${breakpoints.sm}px) {
      padding-left: 0;
    }
  }
  &:last-of-type {
    padding-left: 9px;

    @media screen and (max-width: 1020px) {
      margin-bottom: -180px;
    }
    @media screen and (max-width: 768px) {
      margin-bottom: -100px;
    }

    ${Title},
    ${SubTitle} {
      padding-right: 41px;

      @media screen and (max-width: ${breakpoints.sm}px) {
        padding-right: 0;
      }
    }

    ${Price} {
      padding-right: 20px;
    }
  }
`;

export const Button = styled.a<ButtonProps>`
  display: flex;
  max-width: 316px;
  width: 100%;
  height: 56px;
  margin: 26px auto 0 auto;
  border: 2px solid ${colors.blue};
  border-radius: 28px;
  background-color: ${({ type }) => (type === 'solid' ? colors.blue : 'transparent')};
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: ${({ type }) => (type === 'solid' ? colors.white : colors.blue)};
  font-weight: 600;
  text-transform: uppercase;
  transition: background 0.1s ease-in-out, border 0.1s ease-in-out, color 0.1s ease-in-out;
  will-change: background, color, border;

  @media screen and (max-width: ${breakpoints.lg}px) {
    max-width: calc(100% - 60px);
  }

  &:hover,
  &:focus,
  &:active {
    color: ${colors.white};
    background-color: #1f62ff;
    border-color: #1f62ff;
  }
`;

export const Features = styled.ul`
  margin: 0;
  padding: 0 77px;

  @media screen and (max-width: 768px) {
    padding: 0 30px;
  }
`;

interface FeatureProps {
  type: FeatureType;
}

const TYPE_TO_ICON: Record<FeatureType, string> = {
  additional: plusIcon,
  contains: checkmarkIcon,
  excludes: crossIcon,
};

export const Feature = styled.li<FeatureProps>`
  display: flex;
  margin-bottom: 16px;
  padding-top: 9px;
  align-items: flex-start;
  list-style: none;
  color: ${colors.navyBlue};
  line-height: 1.375;
  text-decoration: ${({ type }) => (type === 'excludes' ? 'line-through' : 'none')};

  &:before {
    content: '';
    width: 39px;
    height: 40px;
    margin-top: -9px;
    margin-right: 12px;
    flex-shrink: 0;
    background-image: url(${({ type }) => TYPE_TO_ICON[type]});
    background-size: contain;
    background-repeat: no-repeat;
    transform: translateY(-4px);
  }

  &:last-of-type {
    margin-bottom: 0;
  }
`;

interface FloatingHeaderProps {
  isVisible: boolean;
}

export const FloatingHeader = styled.header<FloatingHeaderProps>`
  width: 100%;
  height: 118px;
  margin-top: -118px;
  position: sticky;
  top: 0;
  z-index: 99;
  background-color: #fff;
  opacity: 0;
  transform: translateY(-118px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  transition: transform 0.1s ease-in-out;

  @media screen and (max-width: ${breakpoints.lg}px) {
    display: none;
  }

  ${({ isVisible }) =>
    isVisible &&
    `
      transform: translateY(74px);
      opacity: 1;
    `}
`;

export const FloatingHeaderWrapper = styled.div`
  display: grid;
  max-width: 1020px;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  padding: 0 30px;
  grid-template-columns: repeat(2, 1fr);
  box-sizing: border-box;

  @media screen and (max-width: ${breakpoints.lg}px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

export const FloatingHeaderColumn = styled.div`
  width: 100%;
  padding-top: 28px;
  padding-left: 85px;
  position: relative;
  align-self: center;
  justify-self: center;
`;

export const FloatingHeaderName = styled.h2`
  margin: 0 auto 0 0;

  span {
    &:after {
      top: -11px;
      left: -15px;
    }
  }
`;

export const FloatingHeaderPrice = styled.div`
  position: absolute;
  top: 10px;
  right: 76px;
  color: ${colors.navyBlue};
  font-size: ${dimensions.fontSize.medium}px;
  line-height: ${dimensions.lineHeight};
`;

export const FloatingHeaderAmount = styled.span`
  font-size: 2.2em;
  font-weight: 900;
`;

export const FloatingHeaderVault = styled.small`
  margin-right: 4px;
  position: relative;
  top: -14px;
  font-size: 1em;
`;

export const FloatingHeaderButton = styled.a<ButtonProps>`
  display: flex;
  max-width: 316px;
  width: 100%;
  height: 34px;
  margin: 11px 0 19px 0;
  border: 2px solid ${colors.blue};
  border-radius: 28px;
  background-color: ${({ type }) => (type === 'solid' ? colors.blue : 'transparent')};
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: ${({ type }) => (type === 'solid' ? colors.white : colors.blue)};
  font-weight: 600;
  text-transform: uppercase;
  transition: background 0.1s ease-in-out, border 0.1s ease-in-out, color 0.1s ease-in-out;
  will-change: background, color, border;

  &:hover,
  &:focus,
  &:active {
    color: ${colors.white};
    background-color: #1f62ff;
    border-color: #1f62ff;
  }
`;

export const PriceInfo = styled.span`
  display: block;
  margin-top: 16px;
  margin-bottom: 36px;
  color: ${colors.lightGray};
  font-size: ${dimensions.fontSize.regular}px;
  line-height: ${dimensions.lineHeight.regular};
  text-transform: uppercase;
`;

export const PriceAmountSmall = styled.strong`
  display: block;
  margin-top: -20px;
  color: ${colors.darkNavyBlue};
  font-weight: 900;
  font-size: 36px;
  line-height: ${dimensions.lineHeight.heading};
`;

export const RegularPrice = styled.span`
  display: block;
  margin-top: 21px;
`;

export const AppVersion = styled.span`
  display: block;
  margin-top: 8px;
  color: ${colors.darkNavyBlue};
  font-size: 12px;
`;

export const DealIllustration = styled(DealIllustrationRaw)`
  width: 102px;
  height: 38px;
  margin-top: 2px;
  position: absolute;
  right: calc(100% - 14px);
  pointer-events: none;

  @media screen and (max-width: ${breakpoints.lg}px) {
    right: 0;
    bottom: 35px;
  }
`;
